import { DateTime } from 'luxon';
import { cn } from '@Utils/cn';
import type { HotelReportSummary } from '@Types/hotelReport';

/** "Aug 4 – Aug 10" — the stretch of time the digest covers. */
function windowLabel(startIso: string, endIso: string): string {
  const start = DateTime.fromISO(startIso);
  const end = DateTime.fromISO(endIso);
  if (!start.isValid || !end.isValid) return '';
  const sameYear = start.year === end.year;
  return `${start.toFormat('LLL d')} – ${end.toFormat(sameYear ? 'LLL d' : 'LLL d, yyyy')}`;
}

/** "Yesterday · 3:04 PM" — when the digest was generated, which drives feed order. */
function ranLabel(iso: string): string {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return '';
  const cal = dt.toRelativeCalendar();
  const label = cal ? cal.charAt(0).toUpperCase() + cal.slice(1) : '';
  return `${label} · ${dt.toFormat('t')}`;
}

interface Props {
  report: HotelReportSummary;
  onOpen: () => void;
}

export default function HotelReportCard({ report, onOpen }: Props) {
  const { stayCount } = report.stats;
  const countLabel =
    stayCount === 0
      ? 'No stays'
      : `${stayCount} stay${stayCount === 1 ? '' : 's'}`;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'block w-full rounded-xl border border-zinc-200 bg-white p-4 text-left shadow-sm transition',
        'hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-md',
        report.isNew && 'agent-shimmer ring-1 ring-amber-200',
      )}
    >
      <div className="flex items-start justify-between gap-3 text-xs text-zinc-500">
        <span className="flex items-center gap-2">
          <span className="font-medium text-zinc-600">{report.venueName} · {countLabel} booked</span>
          {report.isNew && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold tracking-wide text-amber-700 uppercase">
              New
            </span>
          )}
        </span>
        {/* Run time leads, since it's what the feed is ordered by; the window it
            covers sits underneath so a re-run of an older range still reads clearly. */}
        <span className="shrink-0 text-right">
          <span className="block whitespace-nowrap">
            {ranLabel(report.createdAt)}
          </span>
          <span className="block whitespace-nowrap text-[11px] text-zinc-400">
            covers {windowLabel(report.windowStart, report.windowEnd)}
          </span>
        </span>
      </div>

      {/* the digest's headline, framed as one of the agent's thoughts */}
      <p className="mt-2 flex gap-2 text-sm leading-relaxed text-zinc-800">
        <span aria-hidden className="mt-0.5 shrink-0">
          💭
        </span>
        <span>{report.highlight}</span>
      </p>
    </button>
  );
}
